import api from "@/lib/api";
import { useEventMode } from "@/context/EventModeContext";
import { eventErrorMessage } from "@/lib/events";
import { formatPoints } from "@/lib/pointsFormat";
import { colorForProblemType, problemTypeDisplayName } from "@/utils/api";
import {
    ClockState,
    CompetitionProblemRead,
    ProblemRead,
    ProblemType,
} from "@openapi";
import { TrophyOutlined } from "@ant-design/icons";
import {
    Alert,
    App,
    Button,
    Empty,
    Space,
    Table,
    Tag,
    Tooltip,
    Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

interface EventProblemListProps {
    /** Problem type of the open editor; other types cannot be solved here. */
    problemType: ProblemType | null;
    onSelect: (problem: ProblemRead) => void;
    onChangeEvent: () => void;
}

// The event's problems, in event order, replacing the full problem catalogue
// while the student is in event mode.
export default function EventProblemList({
    problemType,
    onSelect,
    onChangeEvent,
}: EventProblemListProps) {
    const { t } = useTranslation();
    const { message } = App.useApp();
    const { event, loading, error, clockState } = useEventMode();
    const [selectingId, setSelectingId] = useState<string | null>(null);

    const problems = useMemo(
        () =>
            [...(event?.problems ?? [])].sort((a, b) => a.ordinal - b.ordinal),
        [event],
    );
    const waiting = clockState === ClockState.Waiting;

    const handleSelect = useCallback(
        async (problem: CompetitionProblemRead) => {
            setSelectingId(problem.problem_id);
            try {
                const detail = await api.problems.getProblem({
                    problemId: problem.problem_id,
                });
                onSelect(detail);
            } catch (err) {
                message.error(await eventErrorMessage(err, t));
            } finally {
                setSelectingId(null);
            }
        },
        [message, onSelect, t],
    );

    const columns: ColumnsType<CompetitionProblemRead> = useMemo(
        () => [
            {
                title: "#",
                key: "ordinal",
                width: 50,
                render: (_: unknown, __: CompetitionProblemRead, index) =>
                    index + 1,
            },
            {
                title: t("chooseProblemModal.column.name"),
                dataIndex: "name",
                key: "name",
                render: (name: string) => (
                    <Typography.Text strong ellipsis={{ tooltip: name }}>
                        {name}
                    </Typography.Text>
                ),
            },
            {
                title: t("chooseProblemModal.column.type"),
                dataIndex: "problem_type",
                key: "problem_type",
                width: 120,
                render: (type: ProblemType) => (
                    <Tag color={colorForProblemType(type)}>
                        {problemTypeDisplayName(type)}
                    </Tag>
                ),
            },
            {
                title: t("events.problems.points"),
                dataIndex: "points",
                key: "points",
                width: 80,
                render: (points: number) => formatPoints(points),
            },
            {
                title: t("chooseProblemModal.column.action"),
                key: "action",
                width: 120,
                render: (_: unknown, record) => {
                    const otherEditor =
                        problemType !== null &&
                        record.problem_type !== problemType;
                    const reason = waiting
                        ? t("events.problems.notStarted")
                        : otherEditor
                          ? t("events.problems.otherEditor", {
                                type: problemTypeDisplayName(
                                    record.problem_type,
                                ),
                            })
                          : undefined;
                    return (
                        <Tooltip title={reason}>
                            <Button
                                type="primary"
                                size="small"
                                disabled={!!reason}
                                loading={selectingId === record.problem_id}
                                onClick={() => void handleSelect(record)}
                            >
                                {t("chooseProblemModal.select")}
                            </Button>
                        </Tooltip>
                    );
                },
            },
        ],
        [handleSelect, problemType, selectingId, t, waiting],
    );

    return (
        <>
            <Alert
                type="info"
                icon={<TrophyOutlined />}
                showIcon
                title={t("events.problems.restricted", {
                    name: event?.event.name ?? "",
                })}
                action={
                    <Button size="small" onClick={onChangeEvent}>
                        {t("events.problems.changeEvent")}
                    </Button>
                }
                style={{ marginBottom: 12 }}
            />
            {error && (
                <Alert
                    type="error"
                    showIcon
                    title={error}
                    style={{ marginBottom: 12 }}
                />
            )}
            <Table
                dataSource={problems}
                columns={columns}
                rowKey="problem_id"
                loading={loading}
                pagination={false}
                size="small"
                locale={{
                    emptyText: (
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={t("events.problems.empty")}
                        />
                    ),
                }}
            />
            {waiting && (
                <Space style={{ marginTop: 12 }}>
                    <Typography.Text type="secondary">
                        {t("events.problems.waitingHint")}
                    </Typography.Text>
                </Space>
            )}
        </>
    );
}
